import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AdminRoute from "@/components/AdminRoute";
import ErrorBoundary from "@/components/ErrorBoundary";
import SiteModeGate from "@/components/SiteModeGate";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import { Toaster } from "@/components/ui/sonner";
import AuthCallback from "@/components/AuthCallback";
import useSiteMeta from "@/hooks/useSiteMeta";
import {
  PUBLIC_ROUTES,
  PRIVATE_ROUTES,
  ADMIN_ROUTES,
} from "@/routes/routes.config";

// Helpers — turn a ``[{path, element}, ...]`` config array into the actual
// ``<Route>`` JSX. Three small functions instead of one 191-line shell.
const renderPublicRoute = ({ path, element: C }) => (
  <Route key={path} path={path} element={<C />} />
);
const renderPrivateRoute = ({ path, element: C }) => (
  <Route
    key={path}
    path={path}
    element={
      <ProtectedRoute>
        <C />
      </ProtectedRoute>
    }
  />
);
const renderAdminRoute = ({ path, element: C }) => (
  <Route
    key={path}
    path={path}
    element={
      <AdminRoute>
        <C />
      </AdminRoute>
    }
  />
);

function RouterShell() {
  const location = useLocation();
  // Emergent OAuth returns the user with #session_id in the URL fragment.
  // Handle it synchronously (during render) so it runs before any auth checks.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      {PUBLIC_ROUTES.map(renderPublicRoute)}
      {PRIVATE_ROUTES.map(renderPrivateRoute)}
      {ADMIN_ROUTES.map(renderAdminRoute)}
    </Routes>
  );
}

function App() {
  useSiteMeta();
  return (
    <div className="App">
      <ErrorBoundary>
        <BrowserRouter>
          <AuthProvider>
            <SiteModeGate>
              <ImpersonationBanner />
              <RouterShell />
            </SiteModeGate>
          </AuthProvider>
        </BrowserRouter>
        <Toaster richColors position="top-right" closeButton />
      </ErrorBoundary>
    </div>
  );
}

export default App;
